import { customElement } from 'lit/decorators.js';

import JobSchema from '../../../../../models/job-schema.js';
import { personDisplayName } from '../../../../../helpers/person.js';

import NSBankFormJob, { BankFormCopy } from './bank-form-job.js';
import definition from '@job-schemas/company/bank/validation.json';
import { jobSchema } from '../../../job-schema.js';

interface FormValues {
  accountHolder: string;
  accountNumber: string;
  sortCode: string;
}

@customElement(NSCompanyBankValidation.is)
export default class NSCompanyBankValidation extends NSBankFormJob<FormValues> {
  static get is() {
    return 'ns-company-bank-validation';
  }

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { complete: { type: 'hidden' } } });
  }

  async connectedCallback() {
    await super.connectedCallback();

    const me = this._directory?.me;
    this._form.form.change('accountHolder', me ? personDisplayName(me) : '');
  }

  protected get _copy(): BankFormCopy<FormValues> {
    return {
      intro:
        "Adding your company's bank account allows nodeStream to verify your banking details and complete payment-related workflows securely.",
      heading: 'Enter your bank account details',
      fields: [
        { name: 'accountHolder', label: 'Account Holder', requiredMessage: "Please enter the bank's account holder" },
        {
          name: 'accountNumber',
          label: 'Account Number',
          type: 'number',
          requiredMessage: 'Please enter your bank account number',
        },
        { name: 'sortCode', label: 'Sort Code', type: 'number', requiredMessage: 'Please enter your sort code' },
      ],
      lambda: 'bank/add-details',
      loadingMessage: 'Saving your bank details',
      info: [
        {
          label: 'How we verify your account',
          steps: [
            "You enter your company's bank account details here.",
            'nodeStream makes a low-value transfer to that account with a unique reference.',
            'You find the reference on your bank statement and enter it in the next step, to confirm you control the account.',
          ],
        },
        {
          label: 'What you need',
          text: "Your company's account holder name, account number and sort code.",
        },
      ],
    };
  }

  protected _requestBody(values: Partial<FormValues>) {
    if (!this._directory?.me) throw new Error('Missing directory service');

    return {
      personId: this._directory.me.id,
      accountHolder: values.accountHolder,
      accountNumber: values.accountNumber,
      sortCode: values.sortCode,
    };
  }
}
